import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark, Trash2, ExternalLink, Video } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useSavedList, removeSaved } from "@/lib/savedArticles";
import { gazaDateTime } from "@/lib/dateGaza";

const READER = { width: "calc(100% - 32px)", maxWidth: "820px", marginInline: "auto", boxSizing: "border-box" };

/** «الأخبار المحفوظة»: current data for each locally-saved id; removed/unpublished → «لم يعد متاحًا». */
export default function SavedPage() {
    const { t, lang, p } = useLang();
    const saved = useSavedList();
    const [live, setLive] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadErr, setLoadErr] = useState(null);
    const [retry, setRetry] = useState(0);
    const ids = saved.map((s) => s.id).join(",");

    useEffect(() => { document.title = `${t("saved")} | ${t("siteName")}`; }, [lang, t]);
    useEffect(() => {
        if (!ids) { setLive({}); return; }
        setLoading(true); setLoadErr(null);
        api.get(`/public/articles/by-ids?ids=${ids}&lang=${lang}`).then((r) => {
            setLive(Object.fromEntries(r.data.items.map((i) => [i.id, i])));
        }).catch((e) => {
            // Saved list stays usable offline: show the failing request + status so it can be diagnosed, never a fake success.
            const code = e?.response?.status;
            setLoadErr({ code, url: `/api/public/articles/by-ids`, msg: t("loadFail", code) });
        }).finally(() => setLoading(false));
    }, [ids, lang, t, retry]);

    const remove = (id) => { const r = removeSaved(id); if (!r.ok) toast.error(t("saveFail")); else toast(t("removedFromSaved")); };

    return (
        <div style={READER} className="py-6 md:py-8" data-testid="saved-page">
            <div className="flex items-center gap-3 mb-2">
                <div className="h-7 w-1.5 rounded-sm bg-brand-red" />
                <h1 className="font-display font-black text-2xl md:text-3xl text-brand-black dark:text-gray-100 inline-flex items-center gap-2"><Bookmark className="w-6 h-6 text-brand-red" /> {t("saved")} <span className="text-base font-bold text-gray-500" data-testid="saved-count">({saved.length})</span></h1>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-6" data-testid="saved-note">{t("savedNote")}</p>
            {loadErr && (
                <div className="mb-4 rounded-sm border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3 text-xs flex flex-wrap items-center gap-2" role="alert" data-testid="saved-load-error">
                    <span className="font-bold">{loadErr.msg}</span>
                    <span className="text-gray-500" dir="ltr">GET {loadErr.url}{loadErr.code ? ` → ${loadErr.code}` : " → network error"}</span>
                    <button type="button" onClick={() => setRetry((n) => n + 1)} className="ms-auto rounded-sm border border-amber-400 px-2 py-1 font-bold" data-testid="saved-retry">{t("retry")}</button>
                </div>
            )}
            {saved.length === 0 && <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-8 text-center text-gray-500" data-testid="saved-empty">{t("savedEmpty")}</div>}
            <ul className="space-y-3">
                {saved.map((s) => {
                    const a = live[s.id];
                    const unavailable = a && !a.available;
                    const noTr = a?.available && lang !== "ar" && a.translation && !a.translation.available;
                    const href = a?.available ? `${p}/article/${a.slug}` : null;
                    return (
                        <li key={s.id} className="flex gap-3 items-start rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/40 p-3" data-testid="saved-item" data-available={unavailable ? "0" : "1"}>
                            {a?.available && a.cover_image && <img src={imgUrl(a.cover_image, 320)} alt="" className="w-20 h-16 object-cover rounded-sm flex-shrink-0" loading="lazy" />}
                            <div className="flex-1 min-w-0">
                                {unavailable ? (
                                    <div className="font-bold text-gray-500" data-testid="saved-unavailable">{t("savedUnavailable")}<div className="text-xs font-normal mt-1 line-through">{s.title}</div></div>
                                ) : (
                                    <Link to={href || "#"} className="font-display font-bold text-brand-black dark:text-gray-100 hover:text-brand-red leading-snug block" data-testid="saved-link">{a?.title || s.title}</Link>
                                )}
                                <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                                    {a?.published_at && <span>{gazaDateTime(a.published_at, lang)}</span>}
                                    {a?.video_url && <span className="inline-flex items-center gap-1"><Video className="w-3 h-3" /> {t("video")}</span>}
                                    {noTr && <span className="pending-tr" dir="ltr" data-testid="saved-no-translation">{t("arOnly")} · {t("pendingTr")}</span>}
                                    {!a && loading && <span>…</span>}
                                </div>
                            </div>
                            <div className="flex flex-col gap-1 flex-shrink-0">
                                {href && <Link to={href} className="inline-flex items-center gap-1 text-xs font-bold text-brand-red px-2 py-1.5 rounded-sm border border-brand-red/40 hover:bg-red-50 dark:hover:bg-red-950/30 min-h-[36px]" data-testid="saved-open"><ExternalLink className="w-3.5 h-3.5" /> {t("open")}</Link>}
                                <button type="button" onClick={() => remove(s.id)} className="inline-flex items-center gap-1 text-xs font-bold text-gray-600 dark:text-gray-300 px-2 py-1.5 rounded-sm border border-gray-300 dark:border-gray-600 hover:border-brand-red hover:text-brand-red min-h-[36px]" data-testid="saved-remove" aria-label={`${t("remove")}: ${a?.title || s.title}`}><Trash2 className="w-3.5 h-3.5" /> {t("remove")}</button>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
